<template>
  <div>
    <YTable
      v-if="capDefaults"
      class="table-striped table-hover overflow-hidden"
      block-style="min-height: 25rem;"
      :scroll-x="false"
      :data="capDefaults.data"
    >
      <template slot="header-row">
        <th>
          Frequency
        </th>
        <th>
          Type
        </th>
        <th>
          Value
        </th>
        <th></th>
        <th></th>
      </template>
      <template slot="secondHeaderRow">
        <td>
          <Superselect
            title="Frequency"
            v-model="frequency"
            :required="true"
            label="label"
            track-by="value"
            :options="filteredFrequencyOptions"
            class="-mx-3"
          />
        </td>
        <td>
          <Superselect
            title="Type"
            v-model="type"
            :required="true"
            :options="type.length == 0 ? typeOptions : []"
            label="label"
            track-by="value"
            class="-mx-3"
          />
        </td>
        <td>
          <YInput
            label="Value"
            v-model="triggerValue"
            class="-mx-3"
            :required="true"
          />
        </td>
        <td>
          <div class="flex items-center -ml-3 -mr-3">
            <YButton
              @click="createCapDefault"
              color="link"
              class="px-3"
              :is-loading="isUpdating"
              ><Icon name="plus"
            /></YButton>
          </div>
        </td>
        <td>
          <Spinner
            v-if="$apollo.queries.capDefaults.loading"
            color="blue"
            :size="6"
          />
        </td>
      </template>
      <template slot="row" slot-scope="props">
        <td>
          {{ props.rowData.frequency.capitalize() }}
        </td>
        <td>
          {{
            props.rowData.type == 'payout'
              ? props.rowData.type.capitalize()
              : 'Publisher Actions'
          }}
        </td>
        <td>
          <EditableValue
            :key="props.rowData.id"
            :isNumberValue="true"
            type="contentEditable"
            :value="props.rowData.triggerValue"
            :mutation="UPDATE_CAP_DEFAULT_MUTATION"
            :variables="{
              input: { id: props.rowData.id, triggerValue: '{value}' }
            }"
            placeholder="0"
            class="-mr-1 -ml-1"
            value-class="px-1"
            :edit-focus-icon-enabled="false"
            @saved="fireSavedEvent"
          />
        </td>
        <td>
          <a
            class="text-gray-400 hover:text-red-500 cursor-pointer inline-block ml-1"
            @click.prevent="deleteCap(props.rowData.id)"
          >
            <Icon name="trashCan" />
          </a>
        </td>
        <td></td>
      </template>
    </YTable>
    <ValidationErrors
      v-if="validationErrors"
      :errors="validationErrors"
    ></ValidationErrors>
  </div>
</template>

<script>
import Superselect from '../../../components/ui/Superselect.vue'
import ValidationErrorsMixin from '@/mixins/ValidationErrorsMixin'
import DELETE_CAP_DEFAULT_MUTATION from '@/graphql/Caps/DeleteCapDefaultMutation.gql'
import UPDATE_CAP_DEFAULT_MUTATION from '@/graphql/Caps/UpdateCapDefaultMutation.gql'
import CAP_DEFAULTS_QUERY from '@/graphql/Caps/CapDefaultsQuery.gql'
import CREATE_CAP_DEFAULT_MUTATION from '@/graphql/Caps/CreateCapDefaultMutation.gql'

export default {
  mixins: [ValidationErrorsMixin],
  components: { Superselect },
  props: {
    cappableId: {
      type: String,
      required: true
    },
    cappableType: {
      type: String,
      required: true
    },
    triggerableType: {
      type: String,
      default: null
    }
  },
  apollo: {
    capDefaults: {
      query: CAP_DEFAULTS_QUERY,
      variables() {
        return {
          filters: {
            cappableId: { value: [this.cappableId] }
          },
          first: 300
        }
      }
    }
  },
  data() {
    return {
      UPDATE_CAP_DEFAULT_MUTATION,
      isUpdating: false,
      frequencyOptions: [
        {
          value: 'daily',
          label: 'Daily'
        },
        {
          value: 'monthly',
          label: 'Monthly'
        },
        {
          value: 'total',
          label: 'Total'
        }
      ],
      typeOptions: [
        {
          value: 'publisherActions',
          label: 'Publisher Actions'
        },
        {
          value: 'payout',
          label: 'Payout'
        }
      ],
      frequency: [],
      type: [],
      triggerValue: null
    }
  },
  mounted() {
    this.$events.listen('capDefaultDeleted', () => {
      this.$apollo.queries.capDefaults.refetch()
    })
    this.$events.listen('capDefaultCreated', () => {
      this.$apollo.queries.capDefaults.refetch()
    })
  },
  beforeDestroy() {
    this.$events.remove('capDefaultDeleted')
    this.$events.remove('capDefaultCreated')
  },
  computed: {
    filteredFrequencyOptions() {
      const used = this.capDefaults.data.map(item=>item.frequency)
      
      return this.frequencyOptions.filter(option=>!used.includes(option.value))
    }
  },
  methods: {
    fireSavedEvent() {
      this.$events.fire('capDefaultUpdated')
    },
    clearValues() {
      this.frequency = []
      this.type = []
      this.redirects = []
      this.sourceIds = []
      this.triggerValue = null
    },
    createCapDefault() {
      this.isUpdating = true

      this.$apollo
        .mutate({
          mutation: CREATE_CAP_DEFAULT_MUTATION,
          variables: {
            input: {
              cappableId: this.cappableId,
              cappableType: this.cappableType,
              triggerableType: this.triggerableType,
              frequency: this.frequency[0].value,
              type: this.type[0].value,
              triggerValue: Number(this.triggerValue)
            }
          }
        })
        .then(() => {
          this.clearValidationErrors()
          this.clearValues()
          this.isUpdating = false
          this.$toastr.s('Cap Default Created', 'Success')
          this.$nextTick(() => {
            this.$events.emit('capDefaultCreated')
          })
        })
        .catch(error => {
          this.isUpdating = false
          this.setValidationErrors(error)
          console.error(error)
        })
    },
    deleteCap(id) {
      this.$apollo
        .mutate({
          mutation: DELETE_CAP_DEFAULT_MUTATION,
          variables: {
            id: id
          }
        })
        .then(() => {
          this.clearValidationErrors()
          this.$toastr.Add({
            title: 'Deleted',
            msg: 'Cap Default deleted',
            preventDuplicates: true
          })
          this.$events.emit('capDefaultDeleted')
        })
        .catch(error => {
          this.setValidationErrors(error)
          console.error(error)
        })
    }
  }
}
</script>

<style scoped></style>
